/**
 * StrutturaList Component - Hedita Design System
 * Feature: Strutture - Lista strutture
 * Stile: Premium Orange Dark Theme (identico a Prenotazioni)
 */

import { useState } from "react";
import { Plus, Search, Home, ChevronRight, ArrowUpDown, Filter, X, MapPin, Users, BedDouble, Pencil, Trash2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useStrutture, useDeleteStruttura } from "../hooks";
import type { Struttura } from "@/types";

const TIPOLOGIE = ["Tutte", "Villa", "Appartamento", "B&B", "Masseria", "Trullo", "Hotel", "Casa vacanze", "Agriturismo"];
const STATI = ["Attiva", "Inattiva", "In manutenzione"];

const STATO_CONFIG: Record<string, { bg: string; color: string; border: string; dot: string }> = {
  "Attiva": { bg: "bg-emerald-500/10", color: "text-emerald-400", border: "border-emerald-500/20", dot: "bg-emerald-400" },
  "Inattiva": { bg: "bg-slate-700/50", color: "text-slate-400", border: "border-slate-600", dot: "bg-slate-500" },
  "In manutenzione": { bg: "bg-amber-500/10", color: "text-amber-400", border: "border-amber-500/20", dot: "bg-amber-400" },
};

const TIPOLOGIA_STYLES: Record<string, { bg: string; color: string; border: string }> = {
  "Villa": { bg: "bg-violet-500/10", color: "text-violet-400", border: "border-violet-500/20" },
  "Appartamento": { bg: "bg-sky-500/10", color: "text-sky-400", border: "border-sky-500/20" },
  "B&B": { bg: "bg-pink-500/10", color: "text-pink-400", border: "border-pink-500/20" },
  "Masseria": { bg: "bg-orange-500/10", color: "text-orange-400", border: "border-orange-500/20" },
  "Trullo": { bg: "bg-teal-500/10", color: "text-teal-400", border: "border-teal-500/20" },
  "Hotel": { bg: "bg-indigo-500/10", color: "text-indigo-400", border: "border-indigo-500/20" },
  "Casa vacanze": { bg: "bg-lime-500/10", color: "text-lime-400", border: "border-lime-500/20" },
  "Agriturismo": { bg: "bg-green-500/10", color: "text-green-400", border: "border-green-500/20" },
};

const TIPOLOGIA_FALLBACK = [
  { bg: "bg-cyan-500/10", color: "text-cyan-400", border: "border-cyan-500/20" },
  { bg: "bg-amber-500/10", color: "text-amber-400", border: "border-amber-500/20" },
  { bg: "bg-pink-500/10", color: "text-pink-400", border: "border-pink-500/20" },
  { bg: "bg-indigo-500/10", color: "text-indigo-400", border: "border-indigo-500/20" },
];

function getTipologiaStyle(t: string) {
  if (TIPOLOGIA_STYLES[t]) return TIPOLOGIA_STYLES[t];
  const hash = t.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return TIPOLOGIA_FALLBACK[hash % TIPOLOGIA_FALLBACK.length];
}

export function StrutturaList() {
  const [search, setSearch] = useState("");
  const [statoFilter, setStatoFilter] = useState<string | null>(null);
  const [tipologiaFilter, setTipologiaFilter] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [delTarget, setDelTarget] = useState<Struttura | null>(null);
  
  const { data: strutture = [], isLoading } = useStrutture();
  const deleteStruttura = useDeleteStruttura();

  const filtered = strutture.filter((s: Struttura) => {
    const matchSearch = !search || 
      s.nome?.toLowerCase().includes(search.toLowerCase()) ||
      s.citta?.toLowerCase().includes(search.toLowerCase()) ||
      s.indirizzo?.toLowerCase().includes(search.toLowerCase());
    const matchStato = !statoFilter || s.stato === statoFilter;
    const matchTipologia = !tipologiaFilter || s.tipo === tipologiaFilter;
    return matchSearch && matchStato && matchTipologia;
  });

  const handleDelete = async () => {
    if (!delTarget) return;
    deleteStruttura.mutate(delTarget.id, {
      onSuccess: () => setDelTarget(null),
    });
  };

  const totCamere = strutture.reduce((n, s) => n + (s.num_camere || 0), 0);
  const totOspiti = strutture.reduce((n, s) => n + (s.max_ospiti || 0), 0);
  const nAttive = strutture.filter(s => s.stato === "Attiva").length;

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-7 w-40 mb-2" />
            <Skeleton className="h-4 w-56" />
          </div>
          <Skeleton className="h-10 w-36" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
        <Skeleton className="h-96 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground">Strutture</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{strutture.length} strutture · {totCamere} camere · {totOspiti} ospiti max</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2.5 rounded-xl border transition-all ${
              showFilters ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            <Filter className="h-4 w-4" />
          </button>
          <button 
            onClick={() => window.location.href = "/strutture/nuovo"}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-primary/90 transition-all shadow-sm"
          >
            <Plus className="h-4 w-4" /> Nuova
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Totali", value: strutture.length, color: "text-foreground" },
          { label: "Attive", value: nAttive, color: "text-primary" },
          { label: "Camere", value: totCamere, color: "text-emerald-400" },
          { label: "Ospiti max", value: totOspiti, color: "text-muted-foreground" },
        ].map((s, i) => (
          <div key={i} className="bg-card border border-border rounded-xl p-4 text-center hover:border-primary/20 transition-all">
            <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="space-y-3">
          {/* Stati */}
          <div className="flex flex-wrap gap-2 p-3 bg-secondary/30 rounded-xl border border-border/50">
            <button 
              onClick={() => setStatoFilter(null)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                !statoFilter ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"
              }`}
            >
              Tutti
            </button>
            {STATI.map(s => (
              <button 
                key={s} 
                onClick={() => setStatoFilter(statoFilter === s ? null : s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all flex items-center gap-1.5 ${
                  statoFilter === s 
                    ? `${STATO_CONFIG[s].bg} ${STATO_CONFIG[s].color} ${STATO_CONFIG[s].border}` 
                    : "border-border text-muted-foreground"
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${STATO_CONFIG[s].dot}`} />
                {s}
              </button>
            ))}
            {statoFilter && (
              <button 
                onClick={() => setStatoFilter(null)}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Tipologie */}
          <div className="flex flex-wrap gap-2 p-3 bg-secondary/30 rounded-xl border border-border/50">
            <button 
              onClick={() => setTipologiaFilter(null)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                !tipologiaFilter ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"
              }`}
            >
              Tutti
            </button>
            {TIPOLOGIE.slice(1).map(t => (
              <button 
                key={t} 
                onClick={() => setTipologiaFilter(tipologiaFilter === t ? null : t)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                  tipologiaFilter === t 
                    ? `${getTipologiaStyle(t).bg} ${getTipologiaStyle(t).color} ${getTipologiaStyle(t).border}` 
                    : "border-border text-muted-foreground"
                }`}
              >
                {t}
              </button>
            ))}
            {tipologiaFilter && (
              <button 
                onClick={() => setTipologiaFilter(null)}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Cerca per nome, città, indirizzo..."
          className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all"
        />
      </div>

      {/* Lista */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Home className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="font-medium text-foreground mb-1">Nessuna struttura</p>
            <p className="text-sm text-muted-foreground mb-4">Crea la prima struttura</p>
            <button 
              onClick={() => window.location.href = "/strutture/nuovo"}
              className="px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-all"
            >
              Nuova struttura
            </button>
          </div>
        ) : (
          <div className="divide-y divide-border/50">
            {filtered.map((s: Struttura) => {
              const stato = STATO_CONFIG[s.stato || "Attiva"] || STATO_CONFIG["Attiva"];
              const tipo = getTipologiaStyle(s.tipo || "Appartamento");
              
              return (
                <div 
                  key={s.id} 
                  className="p-4 hover:bg-secondary/30 transition-all cursor-pointer group"
                  onClick={() => window.location.href = `/strutture/${s.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Top row */}
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className={`px-2 py-0.5 rounded-lg text-xs font-medium border ${stato.bg} ${stato.color} ${stato.border} flex items-center gap-1.5`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${stato.dot}`} />
                          {s.stato || "Attiva"}
                        </span>
                        <span className={`px-2 py-0.5 rounded-lg text-xs font-medium border ${tipo.bg} ${tipo.color} ${tipo.border}`}>
                          {s.tipo || "Appartamento"}
                        </span>
                        {s.citta && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {s.citta}
                          </span>
                        )}
                      </div>
                      
                      {/* Structure name */}
                      <h3 className="font-semibold text-foreground text-sm mb-1">
                        {s.nome}
                      </h3>
                      
                      {/* Info */}
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        {s.num_camere !== undefined && (
                          <span className="flex items-center gap-1">
                            <BedDouble className="h-3.5 w-3.5" />
                            {s.num_camere} camere
                          </span>
                        )}
                        {s.max_ospiti !== undefined && (
                          <span className="flex items-center gap-1">
                            <Users className="h-3.5 w-3.5" />
                            {s.max_ospiti} ospiti
                          </span>
                        )}
                        {s.indirizzo && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3.5 w-3.5" />
                            {s.indirizzo}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = `/strutture/${s.id}`;
                        }}
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDelTarget(s);
                        }}
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-rose-400 hover:bg-rose-500/10 transition-all"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {delTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-foreground mb-2">Conferma eliminazione</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Sei sicuro di voler eliminare la struttura "{delTarget.nome}"? Questa azione non può essere annullata.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDelTarget(null)}
                className="flex-1 px-4 py-2 border border-border rounded-lg text-sm font-medium hover:bg-muted/50 transition-all"
              >
                Annulla
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteStruttura.isPending}
                className="flex-1 px-4 py-2 bg-rose-500 text-white rounded-lg text-sm font-medium hover:bg-rose-600 transition-all disabled:opacity-50"
              >
                {deleteStruttura.isPending ? "Eliminazione..." : "Elimina"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
